import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import ChangePasswordModal from '../components/ChangePasswordModal/ChangePasswordModal';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'radiologist' | 'technician' | 'viewer';
  permissions: string[];
  lastLogin: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; isFirstTimeLogin?: boolean }>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  sessionInfo: any;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warningSeconds, setWarningSeconds] = useState<number | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showPasswordChangeConfirmation, setShowPasswordChangeConfirmation] = useState(false);

  const authService = AuthService.getInstance();
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      const currentUser = authService.getCurrentUser();
      const session = authService.getSessionInfo();

      setIsAuthenticated(authenticated);
      setUser(currentUser);
      setSessionInfo(session);
      setLoading(false);
    };

    const checkPasswordChangeFlags = async () => {
      try {
        if (!authService.isAuthenticated()) {
          setShowChangePasswordModal(false);
          setShowPasswordChangeConfirmation(false);
          return;
        }

        const [forceChange, reloginRequired] = await Promise.all([
          authService.isForcePasswordChangeRequired(),
          authService.isReloginRequired(),
        ]);

        // relogin-required takes precedence (password already changed, must login again)
        if (reloginRequired) {
          setShowChangePasswordModal(false);
          setShowPasswordChangeConfirmation(true);
          return;
        }

        setShowPasswordChangeConfirmation(false);
        setShowChangePasswordModal(forceChange);
      } catch (e) {
        // If anything fails, don't block the user unexpectedly; log and proceed.
        console.warn('AuthContext: Failed to check password change flags:', e);
      }
    };

    // Handle token expiration
    const handleTokenExpiration = () => {
      // Avoid repeated logouts if we're already logged out / unauthenticated.
      // This can happen if multiple failing requests keep dispatching `token-expired`.
      if (!authService.isAuthenticated()) {
        return;
      }

      logout();
      navigate('/login', { replace: true });
    };

    // Handle session timeout (logout + save already run in AuthService before this event)
    const handleSessionTimeout = () => {
      if (!authService.isAuthenticated()) {
        navigate('/login', { replace: true });
        return;
      }

      void logout();
    };

    const handleSessionWarning = (event: Event) => {
      if (!authService.isAuthenticated()) {
        return;
      }

      const detail = (event as CustomEvent<{ secondsRemaining: number }>).detail;
      const secondsRemaining = detail?.secondsRemaining;

      if (typeof secondsRemaining === 'number') {
        setWarningSeconds(secondsRemaining);
        setShowWarningModal(secondsRemaining > 0);
      }
    };

    const handleSessionWarningClear = () => {
      setWarningSeconds(null);
      setShowWarningModal(false);
    };

    // Wait for session restoration to complete before checking auth
    // This ensures encrypted session data is decrypted before we check authentication
    const initializeAuth = async () => {
      try {
        // Wait for session check to complete
        await authService.waitForSessionCheck();
      } catch (error) {
        console.warn('AuthContext: Error waiting for session check:', error);
      }

      // Check auth status after session restoration
      checkAuth();
      await checkPasswordChangeFlags();
    };

    initializeAuth();

    // Set up periodic checks for session status
    const interval = setInterval(() => {
      checkAuth();
    }, 30000); // Check every 30 seconds

    // Also re-check password-change flags periodically (covers refresh + any late session restore)
    const flagsInterval = setInterval(() => {
      void checkPasswordChangeFlags();
    }, 10000);

    // Listen for token expiration events from ApiService
    window.addEventListener('token-expired', handleTokenExpiration);
    // Listen for session timeout events from AuthService
    window.addEventListener('session-timeout', handleSessionTimeout);
    window.addEventListener('session-warning', handleSessionWarning as EventListener);
    window.addEventListener('session-warning-clear', handleSessionWarningClear);

    return () => {
      clearInterval(interval);
      clearInterval(flagsInterval);
      window.removeEventListener('token-expired', handleTokenExpiration);
      window.removeEventListener('session-timeout', handleSessionTimeout);
      window.removeEventListener('session-warning', handleSessionWarning as EventListener);
      window.removeEventListener('session-warning-clear', handleSessionWarningClear);
    };
  }, [authService, navigate]);

  const login = async (username: string, password: string) => {
    setLoading(true);
    try {
      const result = await authService.login(username, password);

      if (result.success && result.user) {
        setIsAuthenticated(true);
        setUser(result.user);
        setSessionInfo(authService.getSessionInfo());

        // If first time login, show password change modal after navigation
        if (result.isFirstTimeLogin === true) {
          setShowChangePasswordModal(true);
        }
      }

      setLoading(false);
      return result;
    } catch (error) {
      setLoading(false);
      return { success: false, message: 'An error occurred during login' };
    }
  };

  const logout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setSessionInfo(null);
    setShowWarningModal(false);
    setWarningSeconds(null);
    setShowChangePasswordModal(false);
    setShowPasswordChangeConfirmation(false);
    navigate('/login', { replace: true });
  };

  const handleExtendSession = () => {
    authService.updateLastActivity(true);
    setSessionInfo(authService.getSessionInfo());
    setShowWarningModal(false);
    setWarningSeconds(null);
  };

  const handleWarningLogout = async () => {
    await logout();
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const result = await authService.changePassword(currentPassword, newPassword);

    if (result.success) {
      try {
        setShowPasswordChangeConfirmation(true);
        // For any successful password change (first-time or later),
        // require the user to re-login, matching the forced-change flow.
        await authService.setReloginRequired(true);
      } catch (error) {
        // If we fail to persist the flag, at least log it; user will still be logged out
        // when the session naturally expires.
        console.warn('AuthContext: Failed to set relogin-required after password change:', error);
      }
    }

    return result;
  };

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission);
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    changePassword,
    hasPermission,
    hasRole,
    sessionInfo,
    loading,
  };

  const handlePasswordChangeSuccess = () => {
    setShowChangePasswordModal(false);
    setShowPasswordChangeConfirmation(true);
    // Persist relogin-required across refreshes until user confirms and we logout.
    void authService.setReloginRequired(true);
  };

  const handlePasswordChangeConfirmationOK = async () => {
    setShowPasswordChangeConfirmation(false);
    await logout();
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isAuthenticated && showWarningModal && (
        <div className="modal-overlay top-center" role="dialog" aria-modal="true">
          <div className="modal-content session-timeout-modal">
            <div className="modal-header">
              <h3>Session Expiring Soon</h3>
              <button className="admin-button secondary" onClick={handleExtendSession}>×</button>
            </div>
            <div className="modal-body">
              <p>
                You will be logged out in{' '}
                <span className="session-timeout-countdown">{warningSeconds ?? 0}</span>{' '}
                second{(warningSeconds ?? 0) === 1 ? '' : 's'} due to inactivity.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="admin-button secondary"
                onClick={handleWarningLogout}
              >
                Logout Now
              </button>
              <button
                type="button"
                className="admin-button primary"
                onClick={handleExtendSession}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
      {isAuthenticated && showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={handlePasswordChangeSuccess}
          isForced={true}
        />
      )}
      {showPasswordChangeConfirmation && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: 2000 }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '400px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Password Changed Successfully</h3>
            </div>
            <div className="modal-body">
              <p>Your password has been changed successfully. Please login again with your new password.</p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="admin-button primary"
                onClick={handlePasswordChangeConfirmationOK}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
